import { useCallback, useEffect, useState } from 'react';
import { banners as bannersApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can } from '../components/Common';

const API_ROOT = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

export default function Banners() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await bannersApi.list();
      setList(res.data.banners);
    } catch (err) {
      toast.error('Could not load banners', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Banners · UrbanClean'; }, []);

  const remove = async (banner) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await bannersApi.remove(banner._id);
      toast.success('Banner deleted');
      load();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  if (loading) return <Loading label="Loading banners" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Banners</h1>
          <div className="sub">Promotional artwork on the customer app home screen</div>
        </div>
        <Can permission="banner.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New banner</button>
        </Can>
      </div>

      {list.length === 0 ? (
        <div className="card"><Empty title="No banners yet" body="Upload artwork to promote an offer." /></div>
      ) : (
        <div className="grid grid-3">
          {list.map((b) => (
            <div className="card" key={b._id}>
              <img
                src={b.image?.startsWith('http') ? b.image : `${API_ROOT}${b.image}`}
                alt={b.title || 'Banner'}
                style={{ width: '100%', height: 145, objectFit: 'cover', borderRadius: 'var(--radius) var(--radius) 0 0', display: 'block' }}
              />
              <div className="card-body">
                <div className="strong">{b.title || 'Untitled'}</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{b.subtitle}</div>
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="badge badge-gray">{b.position}</span>
                  <span className={`badge badge-${b.isActive ? 'green' : 'gray'}`}>{b.isActive ? 'Live' : 'Hidden'}</span>
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
                  <Can permission="banner.update">
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditing(b)}>Edit</button>
                  </Can>
                  <Can permission="banner.delete">
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => remove(b)}>Delete</button>
                  </Can>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BannerEditor banner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function BannerEditor({ banner, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!banner) return;
    setForm({
      title: banner.title || '', subtitle: banner.subtitle || '',
      position: banner.position || 'home-top',
      actionType: banner.actionType || 'none', actionValue: banner.actionValue || '',
      sortOrder: banner.sortOrder ?? 0, isActive: banner.isActive !== false,
    });
    setFile(null);
  }, [banner]);

  if (!banner) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!banner._id && !file) return toast.error('Choose a banner image');
    setBusy(true);
    try {
      // Sent as multipart so the image rides along with the fields.
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('image', file);

      if (banner._id) await bannersApi.update(banner._id, fd);
      else await bannersApi.create(fd);

      toast.success('Banner saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={banner._id ? 'Edit banner' : 'New banner'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Uploading…' : 'Save banner'}</button>
      </>}>
      <div className="field">
        <label>Image {banner._id ? '(leave blank to keep the current one)' : '*'}</label>
        <input className="input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
        <div className="hint">Recommended 1200 × 500, under 5 MB.</div>
      </div>
      <div className="row">
        <div className="field"><label>Title</label><input className="input" value={form.title || ''} onChange={set('title')} /></div>
        <div className="field"><label>Subtitle</label><input className="input" value={form.subtitle || ''} onChange={set('subtitle')} /></div>
      </div>
      <div className="row">
        <div className="field">
          <label>Position</label>
          <select className="select" value={form.position} onChange={set('position')}>
            <option value="home-top">Home — top</option>
            <option value="home-middle">Home — middle</option>
            <option value="offers">Offers screen</option>
          </select>
        </div>
        <div className="field">
          <label>Tap action</label>
          <select className="select" value={form.actionType} onChange={set('actionType')}>
            <option value="none">Nothing</option>
            <option value="category">Open a category</option>
            <option value="offer">Apply an offer code</option>
            <option value="url">Open a link</option>
          </select>
        </div>
      </div>
      {form.actionType !== 'none' ? (
        <div className="field">
          <label>Action value</label>
          <input className="input" value={form.actionValue || ''} onChange={set('actionValue')} placeholder="Category id, coupon code or URL" />
        </div>
      ) : null}
      <div className="row">
        <div className="field"><label>Sort order</label><input className="input" type="number" value={form.sortOrder} onChange={set('sortOrder')} /></div>
        <div className="field">
          <label>Visibility</label>
          <label className="switch" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
            <span className="track" /><span>Live in the app</span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
